/**
 * Edit the copy of the free-trial renewal reminders (12/7/4 days before the
 * first charge) without a deploy. Asked for by Lizzie and Barb, who rewrite
 * them each month (2026-10-05). One live set for everyone: what's saved here is
 * what the next reminders send; "Reset to original" goes back to the built-in
 * copy. Admin-only, like the rest of this page.
 */
import { useEffect, useState } from 'preact/hooks';
import { rpx, RpxResponse } from 'client/lib/rpx-client';
import { showError } from '@components/app-error';
import { showToast } from '@components/toaster';
import { Button } from '@components/buttons';
import { Spinner } from '@components/spinner';
import { RichHtmlField } from '@components/minidoc/rich-html-field';
import { compactDate } from 'shared/dateutil';

type Data = RpxResponse<typeof rpx.emailDelivery.getTrialReminderTemplates>;
type Reminder = Data['reminders'][0];

export function ReminderCopy() {
  const [data, setData] = useState<Data | undefined>(undefined);
  const [days, setDays] = useState<Reminder['days']>(12);
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [busy, setBusy] = useState<'' | 'save' | 'test' | 'reset'>('');

  const reminder = data?.reminders.find((r) => r.days === days);
  const isDirty = !!reminder && (subject !== reminder.subject || bodyHtml !== reminder.bodyHtml);

  function load(nextDays = days) {
    return rpx.emailDelivery.getTrialReminderTemplates().then((result) => {
      setData(result);
      pick(nextDays, result);
    });
  }

  function pick(nextDays: Reminder['days'], source = data) {
    const next = source?.reminders.find((r) => r.days === nextDays);
    setDays(nextDays);
    setSubject(next?.subject || '');
    setBodyHtml(next?.bodyHtml || '');
  }

  useEffect(() => {
    load().catch(showError);
  }, []);

  function switchTo(nextDays: Reminder['days']) {
    if (nextDays === days) {
      return;
    }
    if (isDirty && !confirm('You have unsaved changes to this email. Discard them?')) {
      return;
    }
    pick(nextDays);
  }

  async function run(kind: typeof busy, action: () => Promise<unknown>) {
    setBusy(kind);
    try {
      await action();
    } catch (err) {
      showError(err);
    } finally {
      setBusy('');
    }
  }

  function save() {
    return run('save', async () => {
      await rpx.emailDelivery.saveTrialReminderCopy({ days, subject, bodyHtml });
      await load(days);
      showToast({
        type: 'ok',
        title: 'Saved',
        message: `The next ${days}-day reminders will use this version.`,
      });
    });
  }

  function sendTest() {
    return run('test', async () => {
      const { sentTo } = await rpx.emailDelivery.sendTrialReminderTest({ days, subject, bodyHtml });
      showToast({ type: 'ok', title: 'Test sent', message: `Sent to ${sentTo}.` });
    });
  }

  function reset() {
    if (
      !confirm(`Go back to the original ${days}-day reminder? Your edited version will be removed.`)
    ) {
      return;
    }
    return run('reset', async () => {
      await rpx.emailDelivery.resetTrialReminderCopy({ days });
      await load(days);
      showToast({ type: 'ok', title: 'Reset', message: 'Back to the original email.' });
    });
  }

  function copyField(name: string) {
    navigator.clipboard
      .writeText(`{{{${name}}}}`)
      .then(() => showToast({ type: 'ok', title: 'Copied', message: `{{{${name}}}}` }))
      .catch(() => {});
  }

  if (!data || !reminder) {
    return (
      <div class="flex justify-center py-12">
        <Spinner />
      </div>
    );
  }

  return (
    <div class="flex flex-col gap-5 max-w-3xl">
      <p class="text-sm text-gray-500">
        These emails go to free-trial members before their first payment. Whatever is saved here is
        what the next reminders send. Use "Send test to me" to check it in your inbox first.
      </p>

      <div class="flex flex-wrap gap-2">
        {data.reminders.map((r) => (
          <Button
            key={r.days}
            class={`px-3 py-2 rounded-md text-sm font-medium border ${
              r.days === days
                ? 'border-indigo-600 bg-indigo-50 text-indigo-700'
                : 'border-gray-200 text-gray-600 hover:border-gray-300'
            }`}
            onClick={() => switchTo(r.days)}
          >
            {r.days} days before
            <span class={`ml-2 text-xs ${r.isEdited ? 'text-indigo-600' : 'text-gray-400'}`}>
              {r.isEdited ? 'Edited' : 'Original'}
            </span>
          </Button>
        ))}
      </div>

      <p class="text-xs text-gray-500 -mt-2">
        {reminder.isEdited
          ? `Edited${reminder.updatedByName ? ` by ${reminder.updatedByName}` : ''}${
              reminder.updatedAt ? ` on ${compactDate(reminder.updatedAt)}` : ''
            }.`
          : 'This is the original email. Saving creates your edited version.'}
      </p>

      <label class="flex flex-col gap-1 text-sm font-medium">
        Subject
        <input
          type="text"
          class="border border-gray-300 rounded-md px-3 py-2 font-normal"
          value={subject}
          maxLength={500}
          onInput={(e) => setSubject((e.target as HTMLInputElement).value)}
        />
      </label>

      <RichHtmlField
        key={`${days}-${reminder.isEdited}-${reminder.updatedAt || ''}`}
        label="Email"
        labelClass="text-sm font-medium"
        minHeightClass="*:min-h-96"
        value={bodyHtml}
        onChange={setBodyHtml}
      />

      <div class="rounded-lg bg-gray-50 p-4 text-sm">
        <p class="font-medium text-gray-700 mb-1">Fill-in fields</p>
        <p class="text-xs text-gray-500 mb-3">
          Type these exactly (or click to copy) and each member sees their own details. They work in
          the subject too. Links go in as normal web addresses.
        </p>
        <ul class="flex flex-col gap-1.5">
          {Object.entries(data.fields).map(([name, description]) => (
            <li key={name} class="flex flex-wrap items-baseline gap-2">
              <Button
                class="font-mono text-xs px-2 py-0.5 rounded bg-white border border-gray-200 hover:border-indigo-300"
                onClick={() => copyField(name)}
              >
                {`{{{${name}}}}`}
              </Button>
              <span class="text-xs text-gray-500">{description}</span>
            </li>
          ))}
        </ul>
      </div>

      <div class="flex flex-wrap items-center gap-3">
        <Button
          class="px-4 py-2 rounded-md bg-indigo-600 text-white text-sm font-medium disabled:opacity-50"
          disabled={!!busy || !isDirty}
          onClick={save}
        >
          {busy === 'save' ? 'Saving…' : 'Save'}
        </Button>
        <Button
          class="px-4 py-2 rounded-md border border-gray-300 text-sm font-medium disabled:opacity-50"
          disabled={!!busy}
          onClick={sendTest}
        >
          {busy === 'test' ? 'Sending…' : 'Send test to me'}
        </Button>
        {isDirty && (
          <Button
            class="px-3 py-2 text-sm text-gray-500 hover:text-gray-700"
            disabled={!!busy}
            onClick={() => pick(days)}
          >
            Discard changes
          </Button>
        )}
        {reminder.isEdited && (
          <Button
            class="px-3 py-2 text-sm text-red-600 hover:text-red-700 ml-auto disabled:opacity-50"
            disabled={!!busy}
            onClick={reset}
          >
            {busy === 'reset' ? 'Resetting…' : 'Reset to original'}
          </Button>
        )}
      </div>
    </div>
  );
}
